import {
	AttachmentStatus,
	AttachmentType,
	FileAttachment,
	UploadableFileAttachment,
} from '@/utils/fetchers/attachment'
import { getImageDimensions, getVideoMetadata } from '@/utils/media'
import uploadFile, { CDNFileType } from '@/utils/upload'
import { useRef } from 'react'

/**
 * 附件上传任务管理器
 */
export default function useAttachmentUploadTaskManager(
	onUpdate: (id: string, attachment: FileAttachment) => void
) {
	const abortControllerMapRef = useRef<Map<string, AbortController>>(new Map())

	// 上传单个附件的函数
	const uploadAttachment = async (attachment: UploadableFileAttachment): Promise<void> => {
		if (!attachment.file) {
			throw new Error('附件文件不存在')
		}

		if (![AttachmentType.PICTURE, AttachmentType.VIDEO].includes(attachment.type)) {
			throw new Error('不支持的附件类型')
		}

		try {
			attachment.status = AttachmentStatus.UPLOADING
			const abortController = new AbortController()
			abortControllerMapRef.current.set(attachment.id, abortController)

			onUpdate(attachment.id, attachment)

			if (attachment.type === AttachmentType.PICTURE) {
				const { width, height } = await getImageDimensions(attachment.file)
				attachment.metadata = { width, height }

				const cdnFile = await uploadFile(attachment.file, {
					provisionConfig: {
						uploadType: CDNFileType.PIC,
						bucket: 'jike',
					},
					signal: abortController.signal,
				})

				attachment.status = AttachmentStatus.UPLOADED
				attachment.cdn = { file: cdnFile }
			}

			if (attachment.type === AttachmentType.VIDEO) {
				// 获取视频元数据
				const { width, height, url } = await getVideoMetadata(attachment.file)
				attachment.metadata = { width, height }

				// 上传视频
				const cdnFile = await uploadFile(attachment.file, {
					provisionConfig: {
						uploadType: CDNFileType.VIDEO,
						bucket: 'videos',
					},
					signal: abortController.signal,
				})

				attachment.status = AttachmentStatus.UPLOADED
				attachment.cdn = { file: { ...cdnFile, fileUrl: url } }
			}

			onUpdate(attachment.id, attachment)
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') {
				return
			}

			attachment.status = AttachmentStatus.UPLOAD_FAILED
			onUpdate(attachment.id, attachment)

			console.error('附件上传失败:', error)

			throw error
		} finally {
			abortControllerMapRef.current.delete(attachment.id)
		}
	}

	const addTask = (attachment: FileAttachment) => {
		attachment.status = AttachmentStatus.WAIT_TO_UPLOAD
		uploadAttachment(attachment as UploadableFileAttachment)
	}

	// 从队列中移除附件（包括中断当前上传）
	const removeTask = (id: string) => {
		// 如果正在上传这个附件，中断上传
		const abortController = abortControllerMapRef.current.get(id)
		if (abortController) {
			abortController.abort()
			abortControllerMapRef.current.delete(id)
		}
	}

	return {
		addTask,
		addTasks: (attachments: FileAttachment[]) => {
			attachments.forEach((item) => {
				addTask(item)
			})
		},
		removeTask,
	}
}
